import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

type CountStatus = 'pending' | 'completed' | 'timed_out' | 'failed';
type JobStatus = 'queued' | 'running' | 'completed' | 'failed';
type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

interface CountResult {
  count: number | null;
  status: CountStatus;
  message?: string | null;
}

interface CleanupStep {
  label: string;
  status: StepStatus;
  total: number | null;
  deleted: number;
  batches: number;
  currentChunk: number;
  totalChunks: number;
  startedAt?: string | null;
  finishedAt?: string | null;
  error?: string | null;
}

interface CleanupJob {
  jobId: string;
  businessId: string;
  status: JobStatus;
  currentStep: string | null;
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  deletedTotal: number;
  counts: Record<string, CountResult>;
  steps: CleanupStep[];
}

interface PreviewResponse {
  counts: Record<string, CountResult>;
}

interface StatusResponse {
  job: CleanupJob | null;
}

interface BusinessCleanupPanelProps {
  businessId: string;
  businessName: string;
}

const COUNT_LABELS: Array<{ key: keyof PreviewResponse['counts']; label: string }> = [
  { key: 'checks', label: 'Checks' },
  { key: 'check_questions', label: 'Check Questions' },
  { key: 'check_question_options', label: 'Question Options' },
  { key: 'safety_checks', label: 'Safety Checks' },
  { key: 'check_responses', label: 'Check Responses' },
];

const STEP_LABELS: Record<string, string> = {
  check_responses: 'Check Responses',
  check_question_options: 'Question Options',
  safety_checks: 'Safety Checks',
  check_questions: 'Check Questions',
  checks: 'Checks',
};

function formatNumber(value: number | null | undefined) {
  if (value == null) return 'Counting...';
  return value.toLocaleString();
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function getStatusColor(status: string): 'default' | 'info' | 'success' | 'warning' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'running' || status === 'queued') return 'info';
  if (status === 'timed_out') return 'warning';
  if (status === 'failed') return 'error';
  return 'default';
}

const BusinessCleanupPanel: React.FC<BusinessCleanupPanelProps> = ({ businessId, businessName }) => {
  const [preview, setPreview] = useState<Record<string, CountResult> | null>(null);
  const [job, setJob] = useState<CleanupJob | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');

  const confirmationPhrase = `DELETE ${businessName || businessId}`;
  const counts = preview ?? job?.counts ?? null;

  const refreshStatus = useCallback(async () => {
    if (!businessId) return;
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<StatusResponse>(
        `${API_BASE_URL}/businesses/${businessId}/check-cleanup/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob(response.data.job);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load cleanup status.');
    } finally {
      setStatusLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!job || (job.status !== 'queued' && job.status !== 'running')) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      refreshStatus();
    }, 3000);

    return () => window.clearInterval(timer);
  }, [job, refreshStatus]);

  const loadPreview = async () => {
    try {
      setPreviewLoading(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      const response = await axios.get<PreviewResponse>(
        `${API_BASE_URL}/businesses/${businessId}/check-cleanup/preview?timeout_ms=120000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(response.data.counts);
      setSuccess('Preview refreshed.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load cleanup preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const startCleanup = async () => {
    try {
      setStartLoading(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      const response = await axios.post<{ job: CleanupJob }>(
        `${API_BASE_URL}/businesses/${businessId}/check-cleanup/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob(response.data.job);
      setSuccess('Business cleanup started.');
      setConfirmationText('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start business cleanup.');
    } finally {
      setStartLoading(false);
    }
  };

  const overallProgress = useMemo(() => {
    if (!job?.steps?.length) return null;
    const knownSteps = job.steps.filter((step) => typeof step.total === 'number');
    if (knownSteps.length === 0) return null;
    const total = knownSteps.reduce((sum, step) => sum + (step.total || 0), 0);
    if (total === 0) return 100;
    const done = knownSteps.reduce(
      (sum, step) => sum + Math.min(step.deleted, step.total || 0),
      0
    );
    return Math.min(100, Math.round((done / total) * 100));
  }, [job]);

  const running = job?.status === 'queued' || job?.status === 'running';

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(128, 128, 128, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'grey.100',
            fontWeight: 700,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.4)',
            borderRadius: 1,
          }}
        >
          Disabled – dangerous
        </Typography>
      </Box>
      <Stack spacing={3} sx={{ pointerEvents: 'none', userSelect: 'none', opacity: 0.85 }}>
      <Alert severity="warning">
        Permanently removes check data for this business from `checks`, `check_questions`,
        `check_question_options`, `safety_checks`, and `check_responses`. This does not touch
        the business record itself.
      </Alert>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview Cleanup Impact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run a preview to count how many rows would be deleted for this business.
                Large tables can take a couple of minutes.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={loadPreview} disabled={previewLoading || running}>
                {previewLoading ? 'Loading Preview...' : 'Preview Counts'}
              </Button>
              <Button variant="text" onClick={refreshStatus} disabled={statusLoading}>
                {statusLoading ? 'Refreshing...' : 'Refresh Status'}
              </Button>
            </Box>

            {counts && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Table</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {COUNT_LABELS.map(({ key, label }) => (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell>{formatNumber(counts[key]?.count)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={counts[key]?.status || 'pending'}
                          color={getStatusColor(counts[key]?.status || 'pending')}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Run Cleanup
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type <strong>{confirmationPhrase}</strong> to enable the cleanup button.
              </Typography>
            </Box>

            <TextField
              label="Confirmation phrase"
              value={confirmationText}
              onChange={(event) => setConfirmationText(event.target.value)}
              fullWidth
              disabled={running}
              helperText="This action permanently deletes the check data previewed above."
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="error"
                onClick={startCleanup}
                disabled={
                  running ||
                  startLoading ||
                  confirmationText.trim() !== confirmationPhrase ||
                  !counts
                }
              >
                {startLoading ? 'Starting...' : 'Start Business Cleanup'}
              </Button>
              {running && <CircularProgress size={24} />}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Cleanup Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Live job status for this business. Polls every 3 seconds while a cleanup is running.
              </Typography>
            </Box>

            {!job ? (
              <Alert severity="info">No cleanup job has been started for this business yet.</Alert>
            ) : (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={job.status} color={getStatusColor(job.status)} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">Current Step</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {job.currentStep ? STEP_LABELS[job.currentStep] || job.currentStep : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">Deleted Rows</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {job.deletedTotal.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">Started</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {formatDate(job.startedAt)}
                    </Typography>
                  </Grid>
                </Grid>

                {overallProgress != null ? (
                  <Box>
                    <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 10, borderRadius: 5 }} />
                    <Typography variant="caption" color="text.secondary">
                      {overallProgress}% across tables with known totals
                    </Typography>
                  </Box>
                ) : running ? (
                  <LinearProgress />
                ) : null}

                {job.error && <Alert severity="error">{job.error}</Alert>}

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Step</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Deleted</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Batches</TableCell>
                      <TableCell>Chunk Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {job.steps.map((step) => (
                      <TableRow key={step.label}>
                        <TableCell>{STEP_LABELS[step.label] || step.label}</TableCell>
                        <TableCell>
                          <Chip size="small" label={step.status} color={getStatusColor(step.status)} />
                        </TableCell>
                        <TableCell>{step.deleted.toLocaleString()}</TableCell>
                        <TableCell>{step.total == null ? '—' : step.total.toLocaleString()}</TableCell>
                        <TableCell>{step.batches.toLocaleString()}</TableCell>
                        <TableCell>
                          {step.totalChunks > 0
                            ? `${step.currentChunk}/${step.totalChunks}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {job.finishedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Finished: {formatDate(job.finishedAt)}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
    </Box>
  );
};

export default BusinessCleanupPanel;
